/**
 * RFP Structure Viewer - Hierarchical display of extracted RFP structure
 * Shows sections, requirements, and evaluation criteria in a collapsible tree
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, CheckSquare, Target, Info } from 'lucide-react';

interface Section {
  name: string;
  heading?: string;
  section_number?: string;
  hierarchy_level?: number;
  parent_section?: string | null;
  content_preview?: string;
  name_normalized?: boolean;
}

interface Requirement {
  text: string;
  section?: string;
  type?: string;
  number?: string;
  id?: string;
  matched_section_number?: string;
  matched_section_name?: string;
}

interface EvaluationCriteria {
  text: string;
  section?: string;
  type?: string;
  number?: string;
  id?: string;
}

interface QualityMetrics {
  coherence_score: number;
  quality_status: string;
  hierarchy_levels: number;
  sections_by_level: Record<string, number>;
  requirements_by_type?: Record<string, number>;
  total_sections: number;
  total_requirements: number;
  total_criteria: number;
}

interface RFPStructureViewerProps {
  sections: Section[];
  requirements: Requirement[];
  evaluation_criteria: EvaluationCriteria[];
  quality_metrics?: QualityMetrics;
  onSectionSelect?: (section: Section) => void;
}

export function RFPStructureViewer({
  sections,
  requirements,
  evaluation_criteria,
  quality_metrics,
  onSectionSelect,
}: RFPStructureViewerProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'sections' | 'requirements' | 'criteria' | 'metrics'>('sections');

  const toggleSection = (sectionNumber: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionNumber)) {
      newExpanded.delete(sectionNumber);
    } else {
      newExpanded.add(sectionNumber);
    }
    setExpandedSections(newExpanded);
  };

  // Build hierarchy from sections
  const buildHierarchy = () => {
    const topLevel: Section[] = [];
    const childrenMap = new Map<string, Section[]>();

    sections.forEach(section => {
      if (!section.parent_section) {
        topLevel.push(section);
      } else {
        if (!childrenMap.has(section.parent_section)) {
          childrenMap.set(section.parent_section, []);
        }
        childrenMap.get(section.parent_section)!.push(section);
      }
    });

    return { topLevel, childrenMap };
  };

  const { topLevel, childrenMap } = buildHierarchy();

  // Recursive section renderer
  const renderSection = (section: Section, level: number = 0) => {
    const hasChildren = section.section_number && childrenMap.has(section.section_number);
    const isExpanded = section.section_number ? expandedSections.has(section.section_number) : false;
    const children = section.section_number ? childrenMap.get(section.section_number) || [] : [];

    // Find requirements for this section
    const sectionRequirements = requirements.filter(req => 
      req.matched_section_number === section.section_number ||
      req.section?.toLowerCase().includes(section.name.toLowerCase().substring(0, 20))
    );

    return (
      <div key={section.section_number || section.name} className="mb-1">
        <div
          className={`flex items-start gap-2 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer ${
            level > 0 ? 'ml-' + (level * 4) : ''
          }`}
          onClick={() => {
            if (hasChildren && section.section_number) {
              toggleSection(section.section_number);
            }
            onSectionSelect?.(section);
          }}
          style={{ paddingLeft: `${level * 1.5}rem` }}
        >
          {hasChildren && (
            <button className="mt-0.5 text-gray-500">
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          )}
          {!hasChildren && <div className="w-4" />}
          
          <FileText className="h-4 w-4 mt-0.5 text-blue-500 flex-shrink-0" />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                {section.name}
              </span>
              {section.hierarchy_level && (
                <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                  L{section.hierarchy_level}
                </span>
              )}
              {sectionRequirements.length > 0 && (
                <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                  {sectionRequirements.length} reqs
                </span>
              )}
            </div>
            {section.content_preview && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                {section.content_preview.substring(0, 100)}...
              </p>
            )}
          </div>
        </div>

        {isExpanded && children.length > 0 && (
          <div className="mt-1">
            {children.map(child => renderSection(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Quality Score Badge
  const getQualityColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
      case 'good': return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
      case 'fair': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
      case 'poor': return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          RFP Structure Analysis
        </h3>
        {quality_metrics && (
          <div className="flex items-center gap-3 mt-2">
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getQualityColor(quality_metrics.quality_status)}`}>
              {quality_metrics.coherence_score}/100 - {quality_metrics.quality_status}
            </span>
            <span className="text-xs text-gray-500">
              {quality_metrics.total_sections} sections
            </span>
            <span className="text-xs text-gray-500">
              {quality_metrics.total_requirements} requirements
            </span>
            <span className="text-xs text-gray-500">
              {quality_metrics.total_criteria} criteria
            </span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'sections'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('sections')}
        >
          <FileText className="inline h-4 w-4 mr-1" />
          Sections ({sections.length})
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'requirements'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('requirements')}
        >
          <CheckSquare className="inline h-4 w-4 mr-1" />
          Requirements ({requirements.length})
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'criteria'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('criteria')}
        >
          <Target className="inline h-4 w-4 mr-1" />
          Criteria ({evaluation_criteria.length})
        </button>
        {quality_metrics && (
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'metrics'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('metrics')}
          >
            <Info className="inline h-4 w-4 mr-1" />
            Metrics
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4 max-h-[600px] overflow-y-auto">
        {activeTab === 'sections' && (
          <div className="space-y-1">
            {topLevel.length > 0 ? (
              topLevel.map(section => renderSection(section))
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No sections found</p>
            )}
          </div>
        )}

        {activeTab === 'requirements' && (
          <div className="space-y-2">
            {requirements.length > 0 ? (
              requirements.map((req, idx) => (
                <div
                  key={idx}
                  className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <CheckSquare className="h-4 w-4 mt-0.5 text-green-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {req.type && (
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            req.type === 'mandatory' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                            req.type === 'desirable' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300'
                          }`}>
                            {req.type}
                          </span>
                        )}
                        {req.number && (
                          <span className="text-xs font-mono px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded">
                            {req.number}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{req.text}</p>
                      {req.matched_section_name && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Section: {req.matched_section_name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No requirements found</p>
            )}
          </div>
        )}

        {activeTab === 'criteria' && (
          <div className="space-y-2">
            {evaluation_criteria.length > 0 ? (
              evaluation_criteria.map((criteria, idx) => (
                <div
                  key={idx}
                  className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-300 dark:hover:border-purple-600 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <Target className="h-4 w-4 mt-0.5 text-purple-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {criteria.type && (
                          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded">
                            {criteria.type}
                          </span>
                        )}
                        {criteria.number && (
                          <span className="text-xs font-mono px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded">
                            {criteria.number}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{criteria.text}</p>
                      {criteria.section && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Section: {criteria.section}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No evaluation criteria found</p>
            )}
          </div>
        )}

        {activeTab === 'metrics' && quality_metrics && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Coherence Score</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {quality_metrics.coherence_score}/100
                </p>
                <p className={`text-sm mt-1 ${getQualityColor(quality_metrics.quality_status).replace('bg-', 'text-').replace('-100', '-700')}`}>
                  {quality_metrics.quality_status.toUpperCase()}
                </p>
              </div>
              
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Hierarchy Depth</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {quality_metrics.hierarchy_levels} levels
                </p>
              </div>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Sections by Level</p>
              <div className="space-y-2">
                {Object.entries(quality_metrics.sections_by_level).map(([level, count]) => (
                  <div key={level} className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 dark:text-gray-300 w-20">Level {level}:</span>
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${(count / quality_metrics.total_sections) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 w-12 text-right">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {quality_metrics.requirements_by_type && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Requirements by Type</p>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(quality_metrics.requirements_by_type).map(([type, count]) => (
                    <div key={type} className="text-center p-3 bg-white dark:bg-gray-900 rounded">
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{count}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 capitalize">{type}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
